// Wording and option tables for the subscription freeze, kept out of the pages so its promises live in
// one reviewable place.

import { SubscriptionFreezeStatus } from 'proto/billing-manager_pb';
import type { FreezeStatusTone } from './components/freezeUi';
import type { SubscriptionFreeze } from './types';

/** The feature's customer-facing name: the header badge while a pause is open, and every eyebrow. */
export const FREEZE_NAME = 'Subscription pause';

export const freezeEyebrow = (section: string) => `${FREEZE_NAME} · ${section}`;

/** A stage of the freeze or resume saga as the customer sees it. `doneAt` is billing-manager's own
 *  `current_step`, so the rail advances on real server progress rather than a timer. */
export type FreezeProgressStage = { id: string; label: string; note: string; doneAt: string };

/** The steps billing-manager and its worker move through, in order: position decides done or running. */
export const FREEZE_STEP_ORDER = ['runtime_blocking', 'runtime_blocked', 'complete'];

/** From this step on the customer may be switched off; before it a pause in progress leaves every page open. */
export const FREEZE_SWITCH_OFF_STEP = 'runtime_blocking';

/** billing-manager-worker's code for a resume stopped only on buying the new dedicated environment. */
export const DEDICATED_ENV_ASSIGN_FAILED_CODE = 'dedicated_env_assign_failed';

// The first four are alternative ways into a resume, all before billing has resumed.
export const RESUME_STEP_ORDER = [
  'scheduled_resume_queued',
  'resume_unpausing',
  'resume_payment_received',
  'resume_payment_expired',
  'runtime_restoring',
  'runtime_restored',
  'complete'
];

export const FREEZE_PROGRESS_STAGES: FreezeProgressStage[] = [
  {
    id: 'runtime',
    label: 'Pausing accounts, copiers and rooms',
    note: 'Your configuration is kept exactly as it is.',
    doneAt: 'runtime_blocked'
  },
  {
    id: 'billing',
    label: 'Pausing your plans',
    note: 'The unused part of your current billing period is credited for your return.',
    doneAt: 'complete'
  }
];

export const RESUME_PROGRESS_STAGES: FreezeProgressStage[] = [
  {
    id: 'billing',
    label: 'Resuming your plans',
    note: 'A new billing period starts today, charged less the credit from when you paused.',
    doneAt: 'runtime_restoring'
  },
  {
    id: 'runtime',
    label: 'Switching accounts, copiers and rooms back on',
    note: 'The same plans, seats and settings you left.',
    doneAt: 'runtime_restored'
  },
  {
    id: 'close',
    label: 'Closing the pause',
    note: 'Your billing date moves to today, and your plans renew from it as usual.',
    doneAt: 'complete'
  }
];

// No IP is held while nobody pays for it: the freeze releases each environment and the resume buys a new one.
const dedicatedEnvAfterResume = (envs: 'it' | 'them') =>
  `Update any broker or prop-firm whitelist first, then move the accounts that were on ${envs} across; until then those accounts stay switched off.`;

const dedicatedEnvRepairCopy = {
  label: 'Support review',
  title: 'Your dedicated environment is being set up',
  body: 'Your plans are back on. Setting up your new dedicated environment needs a support review, and we will finish it for you.',
  tone: 'warning' as FreezeStatusTone
};

export function freezeStatusCopy(freeze: Pick<SubscriptionFreeze, 'status' | 'errorCode'>) {
  if (
    freeze.status === SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_RESUME_REPAIR_REQUIRED &&
    freeze.errorCode === DEDICATED_ENV_ASSIGN_FAILED_CODE
  ) {
    return dedicatedEnvRepairCopy;
  }
  return statusCopy[freeze.status] || statusCopy[SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FROZEN];
}

// Only open freezes come back from billing-manager, so a finished one never reaches this page.
const statusCopy: Record<number, { label: string; title: string; body: string; tone: FreezeStatusTone }> = {
  [SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FREEZING]: {
    label: 'Pausing',
    title: 'Your subscription is being secured',
    body: 'We are pausing active services and preserving your setup. This normally completes shortly.',
    tone: 'ice'
  },
  [SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FROZEN]: {
    label: 'Paused',
    title: 'Your subscription is on hold',
    body: 'Trading actions and copiers are paused. Your connected accounts and configuration remain saved.',
    tone: 'ice'
  },
  [SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_RESUME_PAYMENT_PENDING]: {
    label: 'Payment needed',
    title: 'Your resume payment did not go through',
    body: 'You stay paused until the first payment of your new billing period succeeds. If it stays unpaid, your plans are cancelled on the date in our email.',
    tone: 'warning'
  },
  [SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_RESUMING]: {
    label: 'Resuming',
    title: 'Your subscription is coming back online',
    body: 'Your plans are resuming: the new billing period is charged and your setup switched back on.',
    tone: 'warm'
  },
  [SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_CANCELING]: {
    label: 'Cancelling',
    title: 'Your plans are being cancelled',
    body: 'As you asked, we are ending your paused plans. The unused time you had paid for stays as credit on your account.',
    tone: 'warning'
  },
  [SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_FREEZE_REPAIR_REQUIRED]: {
    label: 'Support review',
    title: 'We are protecting your setup',
    body: 'An automated pause step needs a support review. Please do not repeat the request.',
    tone: 'warning'
  },
  [SubscriptionFreezeStatus.SUBSCRIPTION_FREEZE_STATUS_RESUME_REPAIR_REQUIRED]: {
    label: 'Support review',
    title: 'Your resume needs attention',
    body: 'An automated restoration step needs a support review. Your saved setup remains protected.',
    tone: 'warning'
  }
};

/** Over every page but the pause's own while one is open. A paused customer is a free one, so it says what
 *  is left to them and how to come back. */
export const FREEZE_BANNER = {
  resumesOn: (date: string) => `Your plans resume by themselves on ${date}.`,
  whilePaused: 'Until then you can view your accounts and delete the ones you no longer need.',
  resumeNow: 'Resume now',
  viewStatus: 'View status',
  // In place of a free user's upgrade hint, on a control that would buy a plan.
  resumeToUse: 'Your plan is paused. Resume it to use this.'
};

/** A pause that stopped before taking effect: the plans never paused and the customer is switched back on. */
export const FREEZE_FAILED = {
  label: 'Not paused',
  title: "Your subscription wasn't paused",
  reason:
    'Something went wrong while pausing, so we stopped before pausing anything. Please try again, or contact support if it happens again.',
  unchangedKicker: 'Nothing changed',
  unchangedTitle: 'Your plans are running as before',
  unchanged: 'Nothing was charged for the pause, and your plans carry on and renew as usual.',
  // Past the start of the switch-off a dedicated environment may already have been released.
  dedicatedEnvTitle: 'Check your dedicated environment',
  dedicatedEnv: `If you have a dedicated environment, it may be back as a new one, on a new IP. ${dedicatedEnvAfterResume('it')}`,
  backToBilling: 'Back to billing',
  contactSupport: 'Contact support',
  dismiss: 'Dismiss'
};

/** The status could not be read, so the page cannot say whether a pause is running or how one ended. */
export const FREEZE_STATUS_UNAVAILABLE = {
  title: "We couldn't load your pause status",
  body: 'Check your connection and try again.',
  retry: 'Try again'
};

/** How a resume bills, told the same way on the status and resume pages. */
export const RESUME_BILLING_NOTE =
  'A new billing period starts on the day you resume and is charged less the credit for the time you had left when you paused, so your billing date moves to that day.';

/** What resuming charges today, when billing-manager could price it; the pause credit is already off it. */
export const resumeChargeToday = (amount: string, credit?: string) =>
  credit ? `You pay ${amount} today, after your ${credit} pause credit.` : `You pay ${amount} today.`;

/** The declined resume's payment heading, naming what the payment takes when it is known. */
export const resumePaymentTitle = (amount?: string) =>
  amount ? `Pay ${amount} to switch everything back on` : 'Pay to switch everything back on';

/** Credit that reached the balance after the resume was billed: Stripe never puts it on an open invoice. */
export const RESUME_LATE_CREDIT_NOTE =
  "Your pause credit is already taken off this payment. Other credit on your balance isn't used here; it goes toward your next invoice.";

/** "1 room", "3 rooms" — the one plural rule this flow needs. */
export const pluralCount = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

export const DEDICATED_ENVS_RELEASED_TITLE = 'Dedicated IPs are released';

// The status and resume pages cannot tell who had one (paused plans are not loaded), so they word it as a condition.
export const DEDICATED_ENV_RETURN = 'Any dedicated environment comes back as a new one, on a new IP';

export const DEDICATED_ENV_RESUME_NOTE = `If you had a dedicated environment, it comes back as a new one on a new IP. ${dedicatedEnvAfterResume(
  'it'
)}`;

export const dedicatedEnvsReleasedNotice = (envs: number) => {
  const one = envs === 1;
  const released = one ? 'your dedicated environment and its IP' : `your ${envs} dedicated environments and their IPs`;
  const back = one ? 'a new one on a new IP' : 'new ones on new IPs';
  return `Pausing releases ${released}. When you resume, you get ${back}. ${dedicatedEnvAfterResume(one ? 'it' : 'them')}`;
};

export const ROOMS_AFFECTED_TITLE = 'Your rooms pause too';

/** Members lose a room they did not choose to pause, so the owner is told the cost first. */
export const roomsAffectedNotice = (rooms: number, members: number, resumeAt: string) => {
  const roomLabel = pluralCount(rooms, 'room');
  const audience = members > 0 ? ` and ${pluralCount(members, 'member')}` : '';
  const verb = rooms === 1 && members === 0 ? 'loses' : 'lose';
  return `${roomLabel}${audience} ${verb} access until ${resumeAt}. Seat allocations, member accounts and branding come back exactly as they are now.`;
};
